import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FolderTree, Plus, Trash2, Loader2, Pencil, X, Save, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVEL_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-indigo-50 text-indigo-600',
  'bg-slate-100 text-slate-600',
  'bg-slate-50 text-slate-400',
];

const LEVEL_TEXT = [
  'font-semibold text-slate-900 text-sm',
  'font-medium text-slate-700 text-sm',
  'text-slate-600 text-xs',
  'text-slate-500 text-xs italic',
];

function flattenTree(categories) {
  if (!categories || categories.length === 0) return [];
  const byParent = {};
  for (const c of categories) {
    const pid = c.parent_id || 'root';
    if (!byParent[pid]) byParent[pid] = [];
    byParent[pid].push(c);
  }
  const flat = [];
  const hasKids = {};
  for (const c of categories) {
    if (c.parent_id && c.parent_id !== 'root') hasKids[c.parent_id] = true;
  }
  function walk(pid) {
    const kids = byParent[pid] || [];
    kids.sort((a, b) => a.name.localeCompare(b.name));
    for (const c of kids) {
      flat.push({ ...c, hasChildren: !!hasKids[c.id] });
      walk(c.id);
    }
  }
  walk('root');
  if (flat.length === 0) {
    for (const c of categories.sort((a, b) => a.level - b.level || a.name.localeCompare(b.name))) {
      flat.push({ ...c, hasChildren: !!hasKids[c.id] });
    }
  }
  return flat;
}

export const CategoryTaxonomy = ({ token }) => {
  const [taxonomies, setTaxonomies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTax, setSelectedTax] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [addingCat, setAddingCat] = useState(null);
  const [newCatName, setNewCatName] = useState('');
  const [editingCat, setEditingCat] = useState(null);
  const [editCatName, setEditCatName] = useState('');
  const [collapsed, setCollapsed] = useState({});

  const fetchTaxonomies = useCallback(async () => {
    try {
      const res = await axios.get(API + '/taxonomy', { headers: { Authorization: 'Bearer ' + token } });
      setTaxonomies(res.data.taxonomies || []);
    } catch { toast.error('Failed to load taxonomies'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchTaxonomies(); }, [fetchTaxonomies]);

  const loadTaxonomy = useCallback(async (id) => {
    setSelectedId(id);
    try {
      const res = await axios.get(API + '/taxonomy/' + id, { headers: { Authorization: 'Bearer ' + token } });
      setSelectedTax(res.data.taxonomy);
    } catch { toast.error('Failed to load taxonomy'); }
  }, [token]);

  const handleCreate = async () => {
    if (!newName.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const res = await axios.post(API + '/taxonomy', { name: newName.trim(), description: newDesc.trim() },
        { headers: { Authorization: 'Bearer ' + token } });
      toast.success('Taxonomy created');
      setCreating(false); setNewName(''); setNewDesc('');
      fetchTaxonomies();
      loadTaxonomy(res.data.taxonomy.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDeleteTax = async (id) => {
    try {
      await axios.delete(API + '/taxonomy/' + id, { headers: { Authorization: 'Bearer ' + token } });
      toast.success('Deleted');
      if (selectedId === id) { setSelectedId(null); setSelectedTax(null); }
      fetchTaxonomies();
    } catch { toast.error('Failed'); }
  };

  const handleAddCat = async () => {
    if (!newCatName.trim() || !selectedId || !addingCat) return;
    try {
      await axios.post(API + '/taxonomy/' + selectedId + '/categories', {
        name: newCatName.trim(), level: addingCat.level, parent_id: addingCat.parentId,
      }, { headers: { Authorization: 'Bearer ' + token } });
      toast.success('Added');
      setAddingCat(null); setNewCatName('');
      loadTaxonomy(selectedId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleEditCat = async (catId) => {
    if (!editCatName.trim()) return;
    try {
      await axios.put(API + '/taxonomy/' + selectedId + '/categories/' + catId,
        { name: editCatName.trim() }, { headers: { Authorization: 'Bearer ' + token } });
      setEditingCat(null);
      loadTaxonomy(selectedId);
    } catch { toast.error('Failed'); }
  };

  const handleDeleteCat = async (catId) => {
    try {
      await axios.delete(API + '/taxonomy/' + selectedId + '/categories/' + catId,
        { headers: { Authorization: 'Bearer ' + token } });
      toast.success('Removed');
      loadTaxonomy(selectedId);
    } catch { toast.error('Failed'); }
  };

  const toggleCollapse = (id) => {
    setCollapsed(prev => {
      const n = { ...prev };
      n[id] = !n[id];
      return n;
    });
  };

  const flatNodes = flattenTree(selectedTax?.categories);
  const visibleNodes = [];
  const hiddenParents = new Set();
  for (const node of flatNodes) {
    if (node.parent_id && hiddenParents.has(node.parent_id)) {
      hiddenParents.add(node.id);
      continue;
    }
    if (collapsed[node.id]) hiddenParents.add(node.id);
    visibleNodes.push(node);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 size={32} className="animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="category-taxonomy-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-[#0F172A] mb-2">Category Taxonomy</h1>
          <p className="text-slate-600">Create custom L1-L4 classification taxonomies for bespoke categorization</p>
        </div>
        {!creating && (
          <Button onClick={() => setCreating(true)} className="bg-[#0F172A] hover:bg-[#1e293b] text-white gap-1.5" data-testid="add-taxonomy-btn">
            <Plus size={16} /> New Taxonomy
          </Button>
        )}
      </div>

      {creating && (
        <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4" data-testid="taxonomy-create-form">
          <h3 className="text-sm font-semibold text-slate-900">New Taxonomy</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Name</label>
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Manufacturing Spend Categories"
                className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="taxonomy-name-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Description</label>
              <input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Optional description"
                className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="taxonomy-desc-input" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setCreating(false)}>Cancel</Button>
            <Button size="sm" onClick={handleCreate} disabled={saving} className="bg-[#0F172A] hover:bg-[#1e293b] text-white gap-1" data-testid="taxonomy-save-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <h2 className="font-heading font-semibold text-slate-900 text-sm">Taxonomies</h2>
              <p className="text-[10px] text-slate-500 mt-0.5">{taxonomies.length} total</p>
            </div>
            <div className="p-2 space-y-1 max-h-[60vh] overflow-y-auto" data-testid="taxonomy-list">
              {taxonomies.length === 0 ? (
                <p className="text-xs text-slate-400 p-3 text-center">No taxonomies yet</p>
              ) : taxonomies.map(t => (
                <div key={t.id} className="relative group">
                  <button onClick={() => loadTaxonomy(t.id)}
                    className={'w-full text-left p-3 rounded-sm text-sm transition-all ' +
                      (selectedId === t.id ? 'bg-[#0F172A] text-white' : 'hover:bg-slate-50 text-slate-700')}
                    data-testid={'taxonomy-item-' + t.id}>
                    <div className="font-medium truncate pr-6">{t.name}</div>
                    <div className={'text-[10px] mt-0.5 ' + (selectedId === t.id ? 'text-slate-300' : 'text-slate-400')}>
                      {(t.categories || []).length} categories
                    </div>
                  </button>
                  <button onClick={e => { e.stopPropagation(); handleDeleteTax(t.id); }}
                    className="absolute top-2 right-2 p-1 text-red-400 hover:bg-red-50 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={'taxonomy-delete-' + t.id}>
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          {selectedTax ? (
            <div className="bg-white border border-slate-200 rounded-sm" data-testid="taxonomy-detail">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="font-heading font-semibold text-slate-900">{selectedTax.name}</h2>
                  {selectedTax.description && <p className="text-xs text-slate-500 mt-0.5">{selectedTax.description}</p>}
                </div>
                <Button size="sm" onClick={() => { setAddingCat({ parentId: null, level: 1 }); setNewCatName(''); }}
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-1 text-xs" data-testid="add-l1-btn">
                  <Plus size={12} /> Add L1 Category
                </Button>
              </div>

              <div className="p-4">
                {addingCat && (
                  <div className="flex items-center gap-2 mb-3 p-2 bg-blue-50 border border-blue-200 rounded-sm"
                    style={{ marginLeft: ((addingCat.level - 1) * 24) + 'px' }} data-testid="add-category-form">
                    <span className={'inline-block w-5 h-5 rounded-sm text-center text-[10px] leading-5 font-bold ' + LEVEL_COLORS[addingCat.level - 1]}>
                      {addingCat.level}
                    </span>
                    <input value={newCatName} onChange={e => setNewCatName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleAddCat()}
                      placeholder={'L' + addingCat.level + ' category name'}
                      className="px-2 py-1 border border-blue-300 rounded-sm text-sm flex-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      autoFocus data-testid="new-cat-name-input" />
                    <Button size="sm" onClick={handleAddCat} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7 px-2" data-testid="save-cat-btn">
                      <Save size={12} />
                    </Button>
                    <button onClick={() => setAddingCat(null)} className="p-1 text-slate-400 hover:text-slate-600"><X size={14} /></button>
                  </div>
                )}

                {visibleNodes.length === 0 && !addingCat ? (
                  <div className="text-center py-12">
                    <FolderTree size={40} className="text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500 mb-1">No categories yet</p>
                    <p className="text-xs text-slate-400">Start by adding L1 top-level categories, then nest L2-L4 subcategories</p>
                  </div>
                ) : (
                  <div className="space-y-0.5" data-testid="category-tree">
                    {visibleNodes.map(cat => (
                      <div key={cat.id} style={{ marginLeft: ((cat.level - 1) * 24) + 'px' }} data-testid={'cat-node-' + cat.id}>
                        <div className="flex items-center gap-1.5 py-1.5 group">
                          {(cat.hasChildren || cat.level < 4) ? (
                            <button onClick={() => toggleCollapse(cat.id)} className="p-0.5 text-slate-400 hover:text-slate-600"
                              data-testid={'cat-expand-' + cat.id}>
                              {collapsed[cat.id] ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                            </button>
                          ) : <span className="w-5" />}

                          <span className={'inline-block w-5 h-5 rounded-sm text-center text-[10px] leading-5 font-bold ' + LEVEL_COLORS[cat.level - 1]}>
                            {cat.level}
                          </span>

                          {editingCat === cat.id ? (
                            <div className="flex items-center gap-1 flex-1">
                              <input value={editCatName} onChange={e => setEditCatName(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleEditCat(cat.id)}
                                className="px-2 py-0.5 border border-slate-200 rounded-sm text-sm flex-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                autoFocus data-testid={'cat-edit-input-' + cat.id} />
                              <button onClick={() => handleEditCat(cat.id)} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded-sm"><Save size={12} /></button>
                              <button onClick={() => setEditingCat(null)} className="p-1 text-slate-400 hover:bg-slate-100 rounded-sm"><X size={12} /></button>
                            </div>
                          ) : (
                            <>
                              <span className={LEVEL_TEXT[cat.level - 1]}>{cat.name}</span>
                              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-auto">
                                {cat.level < 4 && (
                                  <button onClick={() => { setAddingCat({ parentId: cat.id, level: cat.level + 1 }); setNewCatName(''); }}
                                    className="p-1 text-blue-500 hover:bg-blue-50 rounded-sm"
                                    data-testid={'cat-add-child-' + cat.id}>
                                    <Plus size={12} />
                                  </button>
                                )}
                                <button onClick={() => { setEditingCat(cat.id); setEditCatName(cat.name); }}
                                  className="p-1 text-slate-400 hover:bg-slate-100 rounded-sm"
                                  data-testid={'cat-edit-' + cat.id}>
                                  <Pencil size={12} />
                                </button>
                                <button onClick={() => handleDeleteCat(cat.id)}
                                  className="p-1 text-red-400 hover:bg-red-50 rounded-sm"
                                  data-testid={'cat-delete-' + cat.id}>
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm p-12 text-center">
              <FolderTree size={48} className="text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Select a taxonomy to view and edit its category tree</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4" data-testid="taxonomy-help">
        <p className="text-xs font-semibold text-slate-700 mb-1">How Bespoke Taxonomies Work</p>
        <p className="text-[11px] text-slate-500">
          Create a custom L1-L4 category hierarchy. When classifying data, select your bespoke taxonomy and the AI will map spend records exclusively to your defined categories instead of generating its own.
        </p>
      </div>
    </div>
  );
};
